import {
    Box,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    Button,
    Paper,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { CounterpartyAddress } from "../counterpartyApi";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";
import { AddressTypeBadge } from "../../common/AddressTypeBadge";
import { Localized } from "@fluent/react";
import AddIcon from '@mui/icons-material/Add'


export const CounterpartyAddressesTab = ({
    addresses,
    onEdit,
    onAdd,
    onDelete,
    onSetPrimary,
}: {
    addresses: CounterpartyAddress[];
    onEdit: (addressId: number) => void;
    onAdd: () => void;
    onDelete: (addressId: number) => void;
    onSetPrimary: (addressId: number) => void;
}) => {
    const hasData = addresses && addresses.length > 0;

    const buildAddressLines = (a: CounterpartyAddress) => {
        const streetLine = [
            a.street,
            a.buildingNo,
            a.apartmentNo ? `/${a.apartmentNo}` : null,
        ]
            .filter(Boolean)
            .join(" ");

        const postalCityLine = [a.postalCode, a.city].filter(Boolean).join(" ");

        const lines: string[] = [];

        if (streetLine) lines.push(streetLine);
        else if (a.line1) lines.push(a.line1);
        else lines.push("—");

        if (streetLine && a.line1) lines.push(a.line1);

        if (a.line2) lines.push(a.line2);

        if (postalCityLine) lines.push(postalCityLine);

        if (a.region) lines.push(a.region);

        return lines;
    };

    return (
        <Stack spacing={2}>
            <Paper variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell width={60}>

                            </TableCell>
                            <TableCell>
                                <Localized id="counterparties-address">
                                    <span>_address</span>
                                </Localized>
                            </TableCell>
                            <TableCell>
                                <Localized id="counterparties-country">
                                    <span>_country</span>
                                </Localized>
                            </TableCell>
                            <TableCell>
                                <Localized id="counterparties-address-type">
                                    <span>_addressType</span>
                                </Localized>
                            </TableCell>
                            <TableCell align="right">

                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {!hasData && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <Stack spacing={1} sx={{ py: 3 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            <Localized id="counterparties-noAddresses">
                                                <span>_Brak adresów</span>
                                            </Localized>
                                        </Typography>

                                        <Button
                                            size="small"
                                            startIcon={<AddOutlinedIcon />}
                                            onClick={onAdd}
                                        >
                                            <Localized id="counterparties-addFirstAddress">
                                                <span>_Dodaj pierwszy adres</span>
                                            </Localized>
                                        </Button>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        )}

                        {hasData &&
                            addresses.map((a) => {
                                const lines = buildAddressLines(a);

                                return (
                                    <TableRow key={a.id} hover>
                                        <TableCell align="center">
                                            <Tooltip
                                                title={
                                                    a.isPrimary ? (
                                                        <Localized id="counterparties-primary-address">
                                                            <span>_To jest adres główny</span>
                                                        </Localized>
                                                    ) : (
                                                        <Localized id="counterparties-setPrimaryAddress">
                                                            <span>_Ustaw jako główny</span>
                                                        </Localized>
                                                    )
                                                }
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        if (!a.isPrimary) onSetPrimary(a.id);
                                                    }}
                                                    sx={{
                                                        color: a.isPrimary ? "#f5b301" : "text.secondary",
                                                        "&:hover": {
                                                            color: "#f5b301",
                                                        },
                                                    }}
                                                >
                                                    {a.isPrimary ? (
                                                        <StarRoundedIcon fontSize="small" />
                                                    ) : (
                                                        <StarOutlineRoundedIcon fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Stack spacing={0.2}>
                                                    {lines.map((l, idx) => (
                                                        <Typography
                                                            key={idx}
                                                            variant={idx === 0 ? "body2" : "caption"}
                                                            color={idx === 0 ? "text.primary" : "text.secondary"}
                                                            sx={{ wordBreak: "break-word" }}
                                                        >
                                                            {l}
                                                        </Typography>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{a.countryCode ?? "—"}</TableCell>
                                        <TableCell>
                                            <AddressTypeBadge type={a.addressType} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">


                                                {/* EDIT */}
                                                <Tooltip
                                                    title={<Localized id="edit"><span>_Edycja</span></Localized>}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onEdit(a.id)}
                                                        sx={{
                                                            color: "text.secondary",
                                                            "&:hover": {
                                                                color: "info.main",
                                                            },
                                                        }}
                                                    >
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                {/* DELETE */}
                                                <Tooltip
                                                    title={<Localized id="delete"><span>_Usuń</span></Localized>}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onDelete(a.id)}
                                                        sx={{
                                                            color: "text.secondary",
                                                            "&:hover": {
                                                                color: "error.main",
                                                            },
                                                        }}
                                                    >
                                                        <DeleteOutlineOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        <TableRow>
                            <TableCell colSpan={5} align="left" sx={{ py: 1 }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={onAdd}
                                    sx={{ px: 1 }}
                                >
                                    <Localized id="add">
                                        <span>_Dodaj</span>
                                    </Localized>
                                </Button>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Paper>
        </Stack>
    );
};